import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RelativeTime } from '@/components/app/relative-time';
import {
  approveFingerprintChangeRequest,
  dismissFingerprintChangeRequest,
  fetchFingerprintChangeRequests,
} from '@/schemas/fingerprint-requests';

/**
 * Pending fingerprint-change requests (#177) — hardware swaps on an activated robot
 * that the server refused (fail-closed) but did not lose. Approving re-pins the
 * licence; dismissing leaves every fingerprint untouched.
 */
export default function FingerprintRequestsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['fingerprint-requests', 'pending'],
    queryFn: () => fetchFingerprintChangeRequests(),
    staleTime: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['fingerprint-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['licenses'] });
  };

  const approve = useMutation({
    mutationFn: approveFingerprintChangeRequest,
    onSuccess: () => {
      toast.success(t('fingerprintRequests.approved'));
      invalidate();
    },
    onError: () => toast.error(t('fingerprintRequests.approveFailed')),
  });

  const dismiss = useMutation({
    mutationFn: dismissFingerprintChangeRequest,
    onSuccess: () => {
      toast.success(t('fingerprintRequests.dismissed'));
      invalidate();
    },
    onError: () => toast.error(t('fingerprintRequests.dismissFailed')),
  });

  const rows = query.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-[19px] font-semibold tracking-tight text-foreground">
          {t('fingerprintRequests.title')}
        </h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          {t('fingerprintRequests.subtitle')}
        </p>
      </header>

      {query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-[13.5px] text-faint">{t('fingerprintRequests.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fingerprintRequests.table.serial')}</TableHead>
              <TableHead>{t('fingerprintRequests.table.components')}</TableHead>
              <TableHead>{t('fingerprintRequests.table.matched')}</TableHead>
              <TableHead>{t('fingerprintRequests.table.seen')}</TableHead>
              <TableHead className="text-right">
                {t('fingerprintRequests.table.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-[13px]">
                  {row.reportedSerial ?? row.installationId ?? row.licenseId}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {row.components.map((component) => (
                      <Badge
                        key={component.componentType}
                        variant={component.matched ? 'secondary' : 'destructive'}
                        title={component.componentValue}
                      >
                        {component.componentType}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-[13px] text-muted-foreground">
                  {row.matchedCount}/{row.components.length}
                  {row.requestCount > 1 && (
                    <span className="ml-1 text-faint">×{row.requestCount}</span>
                  )}
                </TableCell>
                <TableCell className="text-[13px] text-muted-foreground">
                  <RelativeTime date={row.lastSeenAt} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      disabled={approve.isPending || dismiss.isPending}
                      onClick={() => approve.mutate(row.id)}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('fingerprintRequests.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={approve.isPending || dismiss.isPending}
                      onClick={() => dismiss.mutate(row.id)}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('fingerprintRequests.dismiss')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
